import type { Vec2 } from '@sw/schema'

export const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y })
export const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y })
export const scale = (a: Vec2, k: number): Vec2 => ({ x: a.x * k, y: a.y * k })
export const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y
export const length = (a: Vec2): number => Math.hypot(a.x, a.y)
export const distance = (a: Vec2, b: Vec2): number => Math.hypot(b.x - a.x, b.y - a.y)

export function normalize(a: Vec2): Vec2 {
  const len = length(a)
  return len === 0 ? { x: 0, y: 0 } : { x: a.x / len, y: a.y / len }
}

export function lerp(a: Vec2, b: Vec2, t: number): Vec2 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }
}

export const degToRad = (deg: number): number => (deg * Math.PI) / 180
export const radToDeg = (rad: number): number => (rad * 180) / Math.PI

/** Wraps any angle into [0, 360). */
export function normalizeDeg(deg: number): number {
  const wrapped = deg % 360
  return wrapped < 0 ? wrapped + 360 : wrapped
}

/**
 * Smallest signed turn from `a` to `b`, in (-180, 180]. Used wherever headings
 * are compared, so that 359° and 1° read as two degrees apart rather than 358.
 */
export function angleDifferenceDeg(a: number, b: number): number {
  const diff = normalizeDeg(b - a)
  return diff > 180 ? diff - 360 : diff
}

/**
 * Direction from one world point to another, in degrees clockwise from the
 * world's +Y axis — the same convention as a compass bearing once the
 * placement rotation is applied.
 */
export function bearingDeg(from: Vec2, to: Vec2): number {
  const d = sub(to, from)
  return normalizeDeg(radToDeg(Math.atan2(d.x, d.y)))
}

/** Closest point to `p` on the segment `a`–`b`, clamped to the segment. */
export function closestPointOnSegment(p: Vec2, a: Vec2, b: Vec2): { point: Vec2; t: number } {
  const ab = sub(b, a)
  const lengthSquared = dot(ab, ab)
  if (lengthSquared === 0) return { point: a, t: 0 }
  const t = Math.min(1, Math.max(0, dot(sub(p, a), ab) / lengthSquared))
  return { point: add(a, scale(ab, t)), t }
}

export function distanceToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  return distance(p, closestPointOnSegment(p, a, b).point)
}
